import { createContext, CSSProperties, useContext } from 'react';

import { Attributes, mergeAttributes, Node, NodeViewProps } from '@tiptap/core';
import { Attrs } from '@tiptap/pm/model';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { BaseIcon } from '@components/icons/base';
import { IconCloseSmall } from '@components/icons/close-small';
import { IconCompany } from '@components/icons/company';
import { IconInfo } from '@components/icons/info';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconSidemenuRightOpen } from '@components/icons/sidemenu-right-open';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { IconTextShort } from '@components/icons/text-short';
import { IconTraveller } from '@components/icons/traveller';
import { PopoverButton } from '@components/popover-button';
import { Stack } from '@components/stack';
import { Text } from '@components/text/text';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { FieldMappingGroup } from './addons/field-mapping';
import { fieldTypeIconMap } from './addons/field-mapping-type-maps';
import { ActionIcon, actionIcons } from './common';
import { FieldMappingSidebarProps } from './field-mapping-sidebar';

export const isTravellerVariableSource = (variableSource: string) => {
  return (
    variableSource === 'company_traveller' ||
    variableSource === 'traveller' ||
    variableSource === 'traveller_journey' ||
    variableSource.startsWith('company_traveller_attribute') ||
    variableSource.startsWith('journey_attribute')
  );
};

type AttributeNames = string[];

export function createPlaceholderHtml({
  tag,
  attributes,
}: {
  tag: string;
  attributes: AttributeNames;
}) {
  return (attributeObject: { [key in (typeof attributes)[number]]: string }) => {
    return `<${tag} ${attributes
      .map((name) => `${name}="${attributeObject[name]}"`)
      .join(' ')}></${tag}>`;
  };
}

type CreatePlaceholderProps = {
  tag: string;
  attributes: AttributeNames;
  renderText(props: any): any;
  nodeComponent?(props: NodeViewProps): JSX.Element;
};

export const createPlaceholderNode = ({
  tag,
  attributes,
  renderText,
  nodeComponent = PlaceholderContainer,
}: CreatePlaceholderProps) => {
  return Node.create({
    name: tag,
    tag,
    group: 'inline',
    inline: true,
    addAttributes: () => getAttributes(attributes),
    parseHTML: () => [{ tag }],
    renderHTML: (props) => [tag, mergeAttributes(props.HTMLAttributes)],
    renderText: (props) => renderText(props.node.attrs),
    addNodeView: () => ReactNodeViewRenderer(nodeComponent),
  });
};

export const getAttributes = (attributes: string[]): Attributes => {
  return attributes.reduce((accumulated, attributeName) => {
    accumulated[attributeName] = {
      default: null,
      keepOnSplit: true,
      parseHTML: (element) => element.getAttribute(attributeName),
      renderHTML: (elementAttributes) => {
        const attribute = elementAttributes[attributeName];
        return attribute ? { [attributeName]: attribute } : {};
      },
    };
    return accumulated;
  }, {} as Attributes);
};

type VariantAttributes = {
  backgroundColor: Color;
  hoverBackgroundColor: Color;
  borderColor: Color;
  contentColor: Color;
  borderRadius: CSSProperties['borderRadius'];
};

const VARIANT_MAP: Record<string, VariantAttributes> = {
  fieldMapped: {
    backgroundColor: 'blueTrans10',
    hoverBackgroundColor: 'blueTrans20',
    borderColor: 'elementBlue100',
    contentColor: 'elementBlue100',
    borderRadius: pxToRem(16),
  },
  fixed: {
    backgroundColor: 'greenTrans10',
    hoverBackgroundColor: 'greenTrans20',
    borderColor: 'elementGreen100',
    contentColor: 'elementGreen200',
    borderRadius: pxToRem(4),
  },
  error: {
    backgroundColor: 'redTrans10',
    hoverBackgroundColor: 'redTrans20',
    borderColor: 'elementRed100',
    contentColor: 'elementRed100',
    borderRadius: pxToRem(4),
  },
};

type PlaceholderContainerProps = NodeViewProps & {
  icon?: typeof BaseIcon;
  variant?: keyof typeof VARIANT_MAP;
  tooltip?: string;
};

export const PlaceholderContainer = ({
  variant = 'fixed',
  node,
  icon: Icon,
  deleteNode,
  tooltip,
}: PlaceholderContainerProps) => {
  const { contentColor } = VARIANT_MAP[variant];
  const attributes = node.attrs;
  const isFieldMapped = variant === 'fieldMapped';

  const content = (
    <>
      {Icon && <Icon size={16} color={contentColor} />}
      <Text
        style={{ maxWidth: pxToRem(250) }}
        ellipsis
        font="semibold14"
        color={contentColor}
        tooltip={tooltip}
        ellipsisWithTooltip
      >
        {attributes.label ?? attributes.value}
      </Text>
      <RemoveButton
        color={contentColor}
        onClick={(e) => {
          e.stopPropagation();
          deleteNode();
        }}
      />
    </>
  );

  if (!isFieldMapped) {
    return (
      <NodeWrapper className="node-wrapper">
        <ContentContainer $variant={variant}>{content}</ContentContainer>
      </NodeWrapper>
    );
  }

  return (
    <ExpandablePlaceholder node={node} variant={variant}>
      {content}
    </ExpandablePlaceholder>
  );
};

export const FieldMappingContext = createContext({
  showFieldMappingSidebar: (props: Pick<FieldMappingSidebarProps, 'variable'> = {}) => {},
  variableGroups: [] as FieldMappingGroup[],
});

export const useFieldMappingContext = () => {
  return useContext(FieldMappingContext);
};

type ExpandablePlaceholderProps = Pick<PlaceholderContainerProps, 'node' | 'variant'> & {
  children: JSX.Element;
};

const ExpandablePlaceholder = ({ node, variant, children }: ExpandablePlaceholderProps) => {
  const { showFieldMappingSidebar, variableGroups } = useFieldMappingContext();
  const attributes = node.attrs;
  const isTravellerVariable = isTravellerVariableSource(attributes.source);

  const variableGroup = variableGroups.find((group) => {
    if (attributes.action && group.id === attributes.action) {
      return true;
    }
    if (isTravellerVariable) {
      return true;
    }
    if (attributes.source === 'company' && group.id === 'company') {
      return true;
    }
    if (group.id === attributes.source) {
      return true;
    }
    return false;
  });

  const getGroupIcon = (): ActionIcon => {
    if (!variableGroup) {
      return { Icon: IconTextShort };
    }

    if (variableGroup.action) {
      return (
        actionIcons[variableGroup.action] ?? {
          Icon: IconTextShort,
        }
      );
    }

    if (variableGroup.id === 'traveller') {
      return { Icon: IconTraveller };
    }

    if (variableGroup.id === 'company') {
      return { Icon: IconCompany };
    }
    if (variableGroup.id === 'journey') {
      return { Icon: IconJourneyItem };
    }

    return { Icon: IconTextShort };
  };

  const { Icon: GroupIcon, color: groupIconColor } = getGroupIcon();
  const field = variableGroup?.fields.find(
    (field) =>
      (attributes.action !== 'undefined' || attributes.variable?.startsWith(field.source)) &&
      field.field === attributes.field
  );
  const FieldIcon =
    (field && field.type && field.type in fieldTypeIconMap
      ? fieldTypeIconMap[field.type as keyof typeof fieldTypeIconMap]
      : undefined) ?? IconTextShort;

  return (
    <NodeWrapper className="node-wrapper">
      <PopoverButton
        trigger={ContentContainer}
        triggerProps={{
          $variant: variant,
          children,
        }}
        placement="bottom-start"
        contentStyle={{
          minWidth: 'fit-content',
        }}
      >
        {({ close }) => (
          <ExpandedSection>
            <Stack space={2} orientation="horizontal" justify="space-between" align="center">
              <Stack space={1}>
                <Stack orientation="horizontal" space={1} align="center">
                  <GroupIcon size={16} color={groupIconColor ?? 'elementPurple60'} />
                  <Text text={variableGroup?.label} font="regular12" />
                  {variableGroup?.description && (
                    <IconInfo
                      size={16}
                      color="elementPurple50"
                      tooltip={variableGroup.description}
                    />
                  )}
                </Stack>
                <Stack orientation="horizontal" space={1} align="center">
                  <IconSubIndicator color="elementPurple60" size={16} />
                  <FieldIcon size={16} color="elementPurple60" />
                  <Text text={field?.label} font="regular12" />
                </Stack>
              </Stack>
              <IconButton
                size="md"
                variant="ghost-purple"
                icon={<IconSidemenuRightOpen size={24} color="buttonIconPositive" />}
                onClick={() => {
                  showFieldMappingSidebar({
                    variable: field,
                  });
                  close();
                }}
              />
            </Stack>
          </ExpandedSection>
        )}
      </PopoverButton>
    </NodeWrapper>
  );
};

const NodeWrapper = styled(NodeViewWrapper)({
  display: 'inline-block',
  verticalAlign: 'middle',
});

const ContentContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 1,
  className: 'placeholder-container',
})<{
  $variant: PlaceholderContainerProps['variant'];
}>(({ $variant = 'fixed' }) => {
  const variantAttributes = VARIANT_MAP[$variant];
  return {
    backgroundColor: theme.color[variantAttributes.backgroundColor],
    borderRadius: variantAttributes.borderRadius,
    border: `${pxToRem(1)} solid ${theme.color[variantAttributes.borderColor]}`,
    padding: `${pxToRem(0)} ${pxToRem(8)}`,
    height: pxToRem(24),
    cursor: $variant === 'fieldMapped' ? 'pointer' : 'default',
    transition: 'background-color 150ms ease-out',
    '&:hover': {
      backgroundColor: theme.color[variantAttributes.hoverBackgroundColor],
    },
  };
});

const RemoveButton = styled(IconCloseSmall)({
  cursor: 'pointer',
});

const ExpandedSection = styled.div({
  width: pxToRem(340),
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(8),
  padding: `${pxToRem(8)} ${pxToRem(16)}`,
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
});

export const isSourceActionMissing = (attributes: Attrs) => {
  const { source, action } = attributes;
  return (
    (source === 'action' && action.endsWith('_MISSING')) ||
    (source.startsWith('journey_attribute') && source.endsWith('_MISSING'))
  );
};
